<template>
  <aside class="shrink-0 border-r border-cockpit-line bg-cockpit-surface text-cockpit-text transition-[width] duration-200" :class="modelValue ? 'w-72' : 'w-16'">
    <div class="py-1">
      <a
        :href="getItemHref(homeMenuItem)"
        :title="!modelValue ? homeMenuItem.name : null"
        class="mx-1 block cursor-pointer rounded border border-transparent px-2 py-2 text-cockpit-muted transition-colors hover:border-cockpit-line hover:bg-cockpit-green/10 hover:text-cockpit-green focus:outline-none focus:ring-2 focus:ring-cockpit-green"
        :class="{ 'border-cockpit-line bg-cockpit-green/10 text-cockpit-green': isCurrentRoute(homeMenuItem) }"
        @click="handleClick($event, homeMenuItem, [])">
        <span class="flex items-center">
          <i :class="[homeMenuItem.icon, { 'mx-auto': !modelValue, 'mx-2': modelValue }]" class="w-8 p-1 text-center text-xl"></i>
          <span class="grow" v-if="modelValue">{{ homeMenuItem.name }}</span>
        </span>
      </a>
    </div>

    <div v-for="(menuItem, index) in menuList" :key="index">
      <div class="py-1" :class="{ 'bg-cockpit-elevated': menuItem.expanded }">
        <a
          :href="getItemHref(menuItem)"
          :title="!modelValue ? menuItem.name : null"
          class="mx-1 block cursor-pointer rounded border border-transparent px-2 py-2 text-cockpit-muted transition-colors hover:border-cockpit-line hover:bg-cockpit-green/10 hover:text-cockpit-green focus:outline-none focus:ring-2 focus:ring-cockpit-green"
          :class="{ 'border-cockpit-line bg-cockpit-green/10 text-cockpit-green': isCurrentRoute(menuItem) }"
          @click="handleClick($event, menuItem, menuList)">
          <span class="flex items-center">
            <i v-if="menuItem.icon" :class="[menuItem.icon, { 'mx-auto': !modelValue, 'mx-2': modelValue }]" class="w-8 p-1 text-center text-xl"></i>
            <span class="grow" v-if="modelValue">{{ menuItem.name }}</span>
            <span v-if="menuItem.badge && menuItem.badge > 0" class="ml-2 rounded-full border border-cockpit-line bg-cockpit-canvas px-2 py-1 text-xs font-bold text-cockpit-green">
              {{ menuItem.badge }}
            </span>
            <i
              v-if="hasChildren(menuItem) && modelValue"
              class="fa-solid ms-2 transition-transform duration-300"
              :class="menuItem.expanded ? 'fa-chevron-down rotate-180' : 'fa-chevron-down'"></i>
          </span>
        </a>

        <div v-if="menuItem.expanded && modelValue" class="ml-4 w-[calc(100%-1rem)] border-l border-cockpit-line/70 pl-2 pt-1">
          <div v-for="(subItem, subIndex) in menuItem.children" :key="subIndex">
            <div class="py-1" :class="{ 'bg-cockpit-canvas': subItem.expanded }">
              <a
                :href="getItemHref(subItem)"
                :title="!modelValue ? subItem.name : null"
                class="mx-1 block cursor-pointer rounded border border-transparent px-2 py-2 text-cockpit-muted transition-colors hover:border-cockpit-line hover:bg-cockpit-green/10 hover:text-cockpit-green focus:outline-none focus:ring-2 focus:ring-cockpit-green"
                :class="{ 'border-cockpit-line bg-cockpit-green/10 text-cockpit-green': isCurrentRoute(subItem) }"
                @click="handleClick($event, subItem, menuItem.children)">
                <span class="flex items-center">
                  <i v-if="subItem.icon" :class="[subItem.icon, { 'mx-auto': !modelValue, 'mx-2': modelValue }]" class="w-8 p-1 text-center text-xl"></i>
                  <span class="grow" v-if="modelValue">{{ subItem.name }}</span>
                  <span v-if="subItem.badge" class="ml-2 rounded-full border border-cockpit-line bg-cockpit-canvas px-2 py-1 text-xs font-bold text-cockpit-green">
                    {{ subItem.badge }}
                  </span>
                  <i
                    v-if="hasChildren(subItem) && modelValue"
              class="fa-solid ms-2 transition-transform duration-300"
              :class="subItem.expanded ? 'fa-chevron-down rotate-180' : 'fa-chevron-down'"></i>
                </span>
              </a>

              <div v-if="subItem.expanded && modelValue" class="ml-4 border-l border-cockpit-line/50 bg-cockpit-canvas py-1 pl-2">
                <div v-for="(childItem, childIndex) in subItem.children" :key="childIndex">
                  <a
                    :href="getItemHref(childItem)"
                    :title="!modelValue ? childItem.name : null"
                    class="mx-1 block cursor-pointer rounded border border-transparent px-2 py-2 text-cockpit-muted transition-colors hover:border-cockpit-line hover:bg-cockpit-green/10 hover:text-cockpit-green focus:outline-none focus:ring-2 focus:ring-cockpit-green"
                    :class="{ 'border-cockpit-line bg-cockpit-green/10 text-cockpit-green': isCurrentRoute(childItem) }"
                    @click="handleClick($event, childItem, subItem.children)">
                    <span class="flex items-center">
                      <i v-if="childItem.icon" :class="[childItem.icon, { 'mx-auto': !modelValue, 'mx-2': modelValue }]" class="w-8 p-1 text-center text-xl"></i>
                      <span class="grow" v-if="modelValue">{{ childItem.name }}</span>
                      <span v-if="childItem.badge" class="ml-2 rounded-full border border-cockpit-line bg-cockpit-canvas px-2 py-1 text-xs font-bold text-cockpit-green">
                        {{ childItem.badge }}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { get } from '@/crud'

defineProps({
  modelValue: Boolean,
})

const emit = defineEmits(['update:modelValue'])

const store = useStore()
const route = useRoute()
const router = useRouter()
const homeMenuItem = {name: 'Home', icon: 'fa-solid fa-house', screenRoute: '/home', children: []}

const menuList = ref([])
const badges = computed(() => store.state.badges)

const fetchMenu = async () => {
  const response = await get('menu')
  menuList.value = response.data || []
}

const fetchBadges = async () => {
  const response = await get('menu/badges')
  if (!badges.value.status) {
    store.dispatch('setBadges', response)
  }
}

const applyBadges = () => {
  const rawBadges = badges.value || {}
  const badgeKeys = Object.keys(rawBadges)

  for (const menuItem of menuList.value) {
    for (const child of menuItem.children || []) {
      const menuName = child.name?.toLowerCase() || ''
      const badgeKey = badgeKeys.find((key) => menuName.includes(key))

      if (badgeKey) {
        child.badge = rawBadges[badgeKey]
      }
    }
  }
}

const hasChildren = (menuItem) => {
  return Array.isArray(menuItem.children) && menuItem.children.length > 0
}

const isNavigable = (menuItem) => {
  return menuItem.screenRoute && menuItem.screenRoute !== 'null'
}

const isCurrentRoute = (menuItem) => {
  return isNavigable(menuItem) && route.path === menuItem.screenRoute
}

const getItemHref = (menuItem) => {
  if (!isNavigable(menuItem) || hasChildren(menuItem)) {
    return null
  }

  return router.resolve(menuItem.screenRoute).href
}

const closeAllChildren = (menuItem) => {
  for (const child of menuItem.children || []) {
    child.expanded = false
    closeAllChildren(child)
  }
}

const goTo = (menuItem) => {
  if (isNavigable(menuItem)) {
    router.push(menuItem.screenRoute)
  }
}

const handleClick = (event, menuItem, siblings) => {
  if (!event || event.button !== 0) {
    return
  }

  if (hasChildren(menuItem)) {
    event.preventDefault()
    siblings.forEach((item) => {
      if (item !== menuItem) {
        item.expanded = false
        closeAllChildren(item)
      }
    })
    emit('update:modelValue', true)
    menuItem.expanded = !menuItem.expanded
    return
  }

  if (!isNavigable(menuItem)) {
    event.preventDefault()
    return
  }

  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
    return
  }

  event.preventDefault()
  goTo(menuItem)
}

watch(
  badges,
  () => {
    applyBadges()
  },
  { deep: true },
)

watch(
  route,
  async () => {
    await fetchBadges()
    applyBadges()
  },
  { deep: true },
)

onMounted(async () => {
  await fetchMenu()
  await fetchBadges()
  applyBadges()
})
</script>
